import { useState } from "react";
import { Button, Callout, Card, FormField, Modal } from "@promptgatewaysadmin/ui";
import { Link } from "react-router-dom";
import { useSession } from "../auth/SessionProvider.js";
import { useUpdateProfile, useSubscriptionState } from "../queries/hooks.js";
import { capabilities } from "../api/capabilities.js";
import { errorCopy } from "../copy/errors.js";
import { toPortalError, OfflineBanner } from "../components/states.js";

/**
 * /account
 *
 * Fully functional even when the org is `locked` — including deletion. A customer must
 * always be able to leave.
 *
 * There is no email-change control, and that is a decision rather than an omission: a real
 * implementation needs re-auth, verification to the new address, NOTIFICATION TO THE OLD
 * ADDRESS, a Stripe customer-email update, and a decision about pending invites. Five moving
 * parts, each of which fails badly, for a rare need — and the flow is also the exact shape
 * of an account-takeover attempt. Support handles it, which adds a human check.
 *
 * There is no 2FA section either: GCIP multi-factor enrolment is v1.1, and a button that
 * opens nothing is worse than no button.
 */
export function Account() {
  const session = useSession();
  const sub = useSubscriptionState();
  const save = useUpdateProfile();
  const [name, setName] = useState(session.me.displayName ?? "");
  const [saved, setSaved] = useState(false);

  const dirty = name.trim() !== (session.me.displayName ?? "");

  return (
    <div className="pg-page pg-page--narrow">
      <OfflineBanner />

      <Card title="Profile">
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!dirty) return;
            setSaved(false);
            await save.mutateAsync(name.trim());
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
          }}
        >
          <FormField
            label="Display name"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            autoComplete="name"
          />

          {/* Read-only, with the reason. Not a disabled input with no explanation. */}
          <FormField
            label="Email"
            type="email"
            value={session.me.email}
            readOnly
            help="Contact support to change your email address."
          />

          {save.isError && (
            <Callout tone="danger" role="alert">
              {errorCopy(toPortalError(save.error)?.copyKey ?? "error.account.save").body}
            </Callout>
          )}

          <div className="pg-form__actions">
            <Button type="submit" disabled={!dirty || save.isPending}>
              {save.isPending ? "Saving…" : "Save"}
            </Button>
            {/* Confirmation belongs WHERE THE ACTION HAPPENED, not in a toast. */}
            {saved && (
              <span className="pg-saved" role="status">
                Saved
              </span>
            )}
          </div>
        </form>
      </Card>

      <Card title="Password &amp; security">
        <p className="pg-card__body">
          Reset your password by email. You&apos;ll be signed out on other devices.
        </p>
        <Link className="pg-btn pg-btn--secondary" to="/reset">
          Send a reset link
        </Link>
      </Card>

      <DangerZone hasTeam={(sub.data?.seatCount ?? 1) > 1} isAdmin={session.role === "admin"} />
    </div>
  );
}

/**
 * The danger zone.
 *
 * `DELETE /v1/identity/me` does not exist in the merged contract — it is the one gap that
 * still blocks a v1 launch, and it is blocked in turn on a records-retention decision about
 * invoices, which have statutory retention independent of the account. That is a legal
 * question, not an engineering one.
 *
 * So the section RENDERS, with the button disabled and the reason stated. The copy
 * "Nothing else is stored to delete." is unchanged, because it is still true: the platform
 * holds no prompt content to erase.
 */
function DangerZone({ hasTeam, isAdmin }: { hasTeam: boolean; isAdmin: boolean }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");

  const soleAdminBlocked = isAdmin && hasTeam;
  const reason = !capabilities.deleteAccount
    ? "Account deletion isn't available yet — contact support and we'll do it for you."
    : soleAdminBlocked
      ? "You're the only admin of a team with other members. Promote another admin first."
      : null;

  return (
    <Card title="Delete account" className="pg-danger">
      <p className="pg-card__body">Deleting your account:</p>
      <ul className="pg-danger__list">
        <li>signs you out everywhere and disables your login</li>
        <li>removes you from your team and frees your seat</li>
        <li>cancels your subscription at the end of the current period</li>
        <li>keeps your invoices, which we&apos;re required to retain</li>
        <li>
          <strong>Nothing else is stored to delete.</strong> We never held your prompts.
        </li>
      </ul>

      <Button
        variant="danger"
        aria-disabled={reason ? true : undefined}
        aria-describedby={reason ? "delete-reason" : undefined}
        onClick={() => !reason && setOpen(true)}
      >
        Delete my account
      </Button>
      {reason && (
        <p id="delete-reason" className="pg-disabled-reason">
          {reason}
        </p>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Delete your account?"
        description="This can't be undone. Type DELETE to confirm."
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" disabled={typed !== "DELETE"}>
              Delete my account
            </Button>
          </>
        }
      >
        <FormField
          label="Type DELETE to confirm"
          value={typed}
          onChange={(e) => setTyped(e.currentTarget.value)}
        />
      </Modal>
    </Card>
  );
}
